import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, ChevronDown, ChevronUp, Send, Copy, ExternalLink, BellRing, PencilLine, Zap } from "lucide-react";
import { toast } from "sonner";
import { getMyDistribution } from "@/lib/afmApi";
import ReleaseFixPanel from "@/components/ReleaseFixPanel";
import ReleaseReports from "@/components/ReleaseReports";

// Pasos que ve el artista. "rejected" se muestra aparte.
const STEPS = [
  { key: "sent", label: "Enviado a Always Fresh" },
  { key: "approved_internal", label: "Aprobado por Always Fresh" },
  { key: "submitted", label: "Recibido por la distribuidora" },
  { key: "in_review", label: "Revisión automática (hasta 72 h)" },
  { key: "approved", label: "Aprobado por la distribuidora" },
  { key: "forwarded", label: "Enviado a las tiendas (pendiente de publicación)" },
];
const ORDER = { approved_internal: 1, submitted: 2, in_review: 3, approved: 4, forwarded: 5 };
const LANG = { es: "Español", en: "Inglés", pt: "Portugués", fr: "Francés", it: "Italiano", zxx: "Instrumental" };
const GROUPS = { performers: "Intérpretes", studio: "Estudio", label: "Discográfica", composition: "Composición" };
const STORE_NAMES = { spotify: "Spotify", apple_music: "Apple Music", itunes: "iTunes", youtube_music: "YouTube Music", youtube: "YouTube", deezer: "Deezer", amazon_music: "Amazon Music", tidal: "TIDAL", soundcloud: "SoundCloud", pandora: "Pandora", tiktok: "TikTok", audiomack: "Audiomack", anghami: "Anghami", boomplay: "Boomplay", napster: "Napster", claro_musica: "Claro Música" };
const storeName = (k) => STORE_NAMES[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const safeHttps = (u) => typeof u === "string" && u.startsWith("https://");
const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copiado"); };

// Códigos y enlaces REALES que entrega la distribuidora. Si algo aún no existe, no se muestra.
function CodesAndLinks({ dist }) {
  if (!dist) return null;
  const pre = Object.entries(dist.pre_save_links || {}).filter(([, u]) => safeHttps(u));
  const stores = Object.entries(dist.store_links || {}).filter(([, u]) => safeHttps(u));
  const isrcs = (dist.tracks || []).filter((t) => t.isrc);
  if (!dist.upc && !isrcs.length && !pre.length && !stores.length) {
    return <p className="mt-5 rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">Tus códigos (UPC/ISRC) y enlaces aparecerán aquí en cuanto la distribuidora los asigne.</p>;
  }
  return (
    <div className="mt-5 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="artist-codes-links">
      {(dist.upc || isrcs.length > 0) && (
        <div className="space-y-1 text-sm">
          {dist.upc && <div className="flex items-center gap-2"><span className="w-16 text-slate-400">UPC</span><span className="font-mono">{dist.upc}</span><button onClick={() => copy(dist.upc)} title="Copiar"><Copy className="h-3.5 w-3.5 text-slate-400 hover:text-black" /></button></div>}
          {isrcs.map((t) => (
            <div key={t.index ?? t.isrc} className="flex items-center gap-2"><span className="w-16 text-slate-400">ISRC</span><span className="font-mono">{t.isrc}</span><button onClick={() => copy(t.isrc)} title="Copiar"><Copy className="h-3.5 w-3.5 text-slate-400 hover:text-black" /></button><span className="truncate text-xs text-slate-500">{t.title}</span></div>
          ))}
        </div>
      )}
      {pre.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><BellRing className="h-3.5 w-3.5" /> Pre-save</div>
          <div className="flex flex-wrap gap-2">{pre.map(([k, u]) => <a key={k} href={u} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-full bg-black px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800">{storeName(k)} <ExternalLink className="h-3 w-3" /></a>)}</div>
        </div>
      )}
      {stores.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Escúchalo en</div>
          <div className="flex flex-wrap gap-2">{stores.map(([k, u]) => <a key={k} href={u} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:border-black">{storeName(k)} <ExternalLink className="h-3 w-3" /></a>)}</div>
        </div>
      )}
    </div>
  );
}

const list = (v) => (Array.isArray(v) ? v.filter(Boolean).join(", ") : v || "");
const when = (v) => { try { return new Date(v).toLocaleDateString("es-VE", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

function Row({ k, v }) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return null;
  return (
    <div className="flex gap-3 py-1 text-sm">
      <span className="w-36 shrink-0 text-slate-400">{k}</span>
      <span className="min-w-0 break-words text-slate-800">{typeof v === "boolean" ? (v ? "Sí" : "No") : Array.isArray(v) ? list(v) : String(v)}</span>
    </div>
  );
}

export default function ArtistDistribution({ releaseId }) {
  const [d, setD] = useState(undefined);
  const [open, setOpen] = useState(false);
  const reload = () => getMyDistribution(releaseId).then(setD).catch(() => setD(null));
  useEffect(() => { reload(); }, [releaseId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;

  const step = d.state ? ORDER[d.state] ?? 0 : 0;
  const rejected = d.state === "rejected";
  const inAutoReview = ["submitted", "in_review"].includes(d.state) && d.review?.status !== "needs_fix" && d.review?.status !== "manual";
  const submittedAt = (d.history || []).find((x) => x.event === "submitted")?.at;
  const etaDate = submittedAt ? new Date(new Date(submittedAt).getTime() + 72 * 3600 * 1000) : null;
  const dateOf = (key) => {
    const ev = key === "approved_internal" ? "approved" : key === "submitted" ? "submitted" : `status:${key}`;
    const h = (d.history || []).find((x) => x.event === ev);
    return h ? when(h.at) : "";
  };
  const data = d.data || {};
  const md = data.metadata || {};
  const dist = md.distribution || {};
  const rights = md.rights || {};

  return (
    <div className="space-y-6">
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="artist-distribution">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-black"><Send className="h-5 w-5 text-[#AC3333]" /> Estado de tu distribución</h3>
        {!d.state && (
          <Link to={`/distribute?editar=${releaseId}`} data-testid="edit-release" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-black">
            <PencilLine className="h-3.5 w-3.5" /> Editar lanzamiento
          </Link>
        )}
      </div>
      {d.send_issue && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="send-issue">
          <div className="font-semibold">La distribuidora pidió corregir un dato</div>
          <p className="mt-1">{d.send_issue}</p>
          <Link to={`/distribute?editar=${releaseId}`} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-black px-3 py-1.5 text-xs font-semibold text-white"><PencilLine className="h-3.5 w-3.5" /> Corregir y reenviar</Link>
        </div>
      )}
      {!d.state && <p className="-mt-2 mb-4 text-xs text-slate-500">Aún no sale a la distribuidora: puedes cambiar cualquier dato. Cuando ya esté enviado, los cambios se piden con “Solicitar corrección”.</p>}

      {d.review?.status === "needs_fix" && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="review-fix">
          <div className="mb-1 font-semibold">Tu lanzamiento está detenido: corrige esto y vuelve a enviarlo</div>
          <ul className="list-disc pl-5">{d.review.issues.map((i, k) => <li key={k}>{i}</li>)}</ul>
          {!d.state && <ReleaseFixPanel releaseId={releaseId} title={data.title || md.title || ""} migration={md.submission_type === "migration"} prevLabel={(md.migration || {}).previous_distributor || ""} onDone={reload} />}
        </div>
      )}
      {inAutoReview && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4" data-testid="auto-approval-note">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600"><Zap className="h-[18px] w-[18px]" /></span>
          <div className="min-w-0">
            <div className="font-semibold text-emerald-900">Aprobación automática · hasta 72 h</div>
            <p className="mt-0.5 text-sm leading-relaxed text-emerald-800/80">La distribuidora revisa y aprueba tu lanzamiento de forma automática. Si todo está en orden, pasa solo a las tiendas, sin esperas manuales.{etaDate ? <> Aprobación estimada antes del <strong className="font-semibold text-emerald-900">{when(etaDate)}</strong>.</> : ""}</p>
          </div>
        </div>
      )}
      {d.review?.status === "manual" && <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Un detalle de tu lanzamiento quedó para una revisión rápida de nuestro equipo (por ejemplo, confirmar un artista invitado). Te avisaremos si hace falta algo; si no, sigue solo.</div>}
      {d.review?.suggestions?.length > 0 && d.review.status !== "needs_fix" && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"><span className="font-semibold">Sugerencias para próximos lanzamientos:</span><ul className="mt-1 list-disc pl-5">{d.review.suggestions.map((i, k) => <li key={k}>{i}</li>)}</ul></div>
      )}
      {rejected ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">La distribuidora pidió correcciones. Nuestro equipo te contactará con los detalles.</div>
      ) : (
        <ol className="space-y-3">
          {STEPS.map((s, i) => {
            const done = i <= step;
            const current = i === step;
            return (
              <li key={s.key} className="flex items-center gap-3">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${done ? "bg-emerald-500 text-white" : "border border-slate-300 text-slate-300"}`}>
                  {done ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-2 w-2" />}
                </span>
                <span className={`text-sm ${current ? "font-semibold text-black" : done ? "text-slate-700" : "text-slate-400"}`}>{s.label}</span>
                {done && i > 0 && dateOf(s.key) && <span className="text-xs text-slate-400">{dateOf(s.key)}</span>}
              </li>
            );
          })}
        </ol>
      )}

      {["approved", "forwarded"].includes(d.state) && <CodesAndLinks dist={d.distribution} />}

      <button onClick={() => setOpen(!open)} className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-black" data-testid="artist-distribution-toggle">
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />} {open ? "Ocultar" : "Ver"} la información que enviaste
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          <div>
            <Row k="Artista(s)" v={md.primary_artists || data.primary_artist} />
            <Row k="Featuring" v={md.featured_artists} />
            <Row k="Fecha de salida" v={data.release_date} />
            <Row k="Formato" v={md.format === "album" ? "Álbum" : md.format === "ep" ? "EP" : md.format ? "Single" : ""} />
            <Row k="Género" v={[md.primary_genre, md.subgenre].filter(Boolean).join(" · ")} />
            <Row k="Idioma" v={LANG[md.language] || md.language} />
            <Row k="Sello" v={data.label} />
            <Row k="UPC" v={d.distribution?.upc || data.upc || "Se asigna automáticamente"} />
            <Row k="Tiendas" v={dist.mode === "custom" ? dist.stores : "Todas las tiendas"} />
            <Row k="Territorios" v={dist.territories?.mode === "exclude" ? `Todo el mundo excepto: ${list(dist.territories?.excluded_countries)}` : "Todo el mundo"} />
            <Row k="© / ℗" v={[rights.c_line, rights.p_line].filter(Boolean).join("  ·  ")} />
            <Row k="Editora" v={rights.publisher} />
            <Row k="Sociedad (PRO)" v={rights.pro} />
          </div>
          {(data.tracks || []).map((t, i) => (
            <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-4">
              <div className="mb-2 font-semibold text-black">{i + 1}. {t.title}{t.version ? ` (${t.version})` : ""}{t.explicit ? <span className="ml-2 rounded bg-black px-1.5 py-0.5 text-[10px] text-white">E</span> : null}</div>
              <Row k="ISRC" v={d.distribution?.tracks?.[i]?.isrc || t.isrc || "Se asigna automáticamente"} />
              <Row k="Featuring" v={t.featured_artists} />
              <Row k="Compositores" v={t.composers} />
              <Row k="Productores" v={t.producers} />
              <Row k="Inicio TikTok" v={t.tiktok_preview_start} />
              {Object.entries(GROUPS).map(([g, label]) => (
                <Row key={g} k={label} v={(t.contributors?.[g] || []).map((c) => (c.role ? `${c.name} (${c.role})` : c.name))} />
              ))}
            </div>
          ))}
          {(!rights.publisher || !rights.pro) && (
            <p className="text-xs text-slate-500">¿Tienes editora o estás inscrito en una sociedad de gestión (SACVEN, ASCAP, BMI…)? Escríbenos por soporte para agregarlo y cobrar también tus derechos de autor.</p>
          )}
        </div>
      )}
    </div>
    {d.state === "forwarded" && <ReleaseReports releaseId={releaseId} />}
    </div>
  );
}
